import { dataDogStatus } from '../constants';
import { getDataDogLogger } from './getDataDogLogger';

const { performanceLogger } = getDataDogLogger();

/**
 * Performance logger makes it simple to track performance related operations in
 * a consistent manner.
 *
 * Loggers are created with a main "topic" and one or more "sub topics". The
 * main topic should be the high level "thing" you are measuring, .e.g
 * 'ContentPage', and sub topics should specify the more granular operation
 * being measured, e.g. a sanity fetch for X, a shopify fetch for Y.
 *
 * Logs are sent to DataDog.
 *
 * ## Example ##
 * ```ts
 * const sanityLogger = newPerformanceLogger('MyEpicPage')
 *   .addData({ slug, locale })
 *   .child('sanity');
 *
 * const [data1, data2, data3] = await Promise.all([
 *   sanityLogger.time('sanityFetch1', sanityFetch1(...)),
 *   sanityLogger.time('sanityFetch2', sanityFetch2(...)),
 *   sanityLogger.time('sanityFetch3', sanityFetch3(...))
 * ]);
 * ```
 *
 * This would log 3 logs to DataDog, all with the topic "MyEpicPage" and first
 * sub topic being "sanity". the second sub topic being "sanityFetch1",
 * "sanityFetch2" or "sanityFetch3". All log entries would include the slug and
 * locale as well.
 */
export type PerformanceLogger = {
  /**
   * Logs how long a promise takes to resolve (or reject).
   *
   * @param subTopic - The sub topic of the operation being measured.
   * @param promise - The promise to measure.
   * @param data - Additional data to include in the log.
   */
  time: <T>(subTopic: string, promise: Promise<T>, data?: Record<string, any>) => Promise<T>;
  /**
   * Adds data to log by default for this logger.
   */
  addData: (data: Record<string, any>) => PerformanceLogger;
  /**
   * Replaces the data to log by default for this logger. To clear the data pass
   * an empty object.
   */
  setData: (data: Record<string, any>) => PerformanceLogger;
  /**
   * Create a copy of this logger with an additional sub topic.
   *
   * This is useful if you need to log multiple things for the same topic.
   */
  child: (subTopic?: string) => PerformanceLogger;
};

export type PerformanceLoggerOpts = {
  subTopics?: string[];
  defaultData?: Record<string, any>;
};

export function newPerformanceLogger(
  topic: string,
  { subTopics, defaultData }: PerformanceLoggerOpts = {}
): PerformanceLogger {
  const mySubtopics = [...(subTopics ?? [])];
  let myDefaultData = { ...(defaultData ?? {}) };

  return {
    async time<T>(subTopic: string, promise: Promise<T>, data?: Record<string, any>) {
      const start = new Date();

      promise.finally(() => {
        const end = new Date();
        const tookMs = end.getTime() - start.getTime();
        const dataFields = { ...myDefaultData, ...(data ?? {}) };

        performanceLogger.log({
          message: `Performance log: ${topic}`,
          topic,
          subTopics: [...mySubtopics, subTopic],
          level: dataDogStatus.INFO,
          timing: {
            start: start.toISOString(),
            end: end.toISOString(),
            tookMs
          },
          data: Object.keys(dataFields).length ? dataFields : undefined
        });
      });

      return promise;
    },

    addData(data: Record<string, any>) {
      myDefaultData = { ...myDefaultData, ...data };
      return this;
    },

    setData(data: Record<string, any>) {
      myDefaultData = { ...data };
      return this;
    },

    child: (subTopic?: string) =>
      newPerformanceLogger(topic, {
        subTopics: subTopic ? [...mySubtopics, subTopic] : mySubtopics,
        defaultData: myDefaultData
      })
  };
}

export const noopPerformanceLogger: PerformanceLogger = {
  async time<T>(_subTopic: string, promise: Promise<T>, _data?: Record<string, any>) {
    return promise;
  },

  addData() {
    return this;
  },
  setData() {
    return this;
  },
  child() {
    return this;
  }
};
